import { api } from '@/services/api/apiClient';
import type {
  AnalysisStatusResponse,
  Industry,
  StartAnalysisResponse,
  StoreReportResponse,
  UserContext,
} from '@/features/discovery/types/discovery.types';

/**
 * All Deep Analysis API calls require a validated API access token.
 * The backend extracts tenant_id, user_id, and email from the validated JWT claims.
 */

function authHeaders(apiToken: string) {
  return { headers: { Authorization: `Bearer ${apiToken}` } };
}

/** Response from the eligibility check */
export interface DeepScanEligibilityResponse {
  tenantId: string;
  deepScanEnabled: boolean;
}

/**
 * Check if the caller's tenant has Deep Scan enabled.
 */
export async function checkDeepScanEligibility(
  apiToken: string
): Promise<DeepScanEligibilityResponse> {
  const response = await api.get<DeepScanEligibilityResponse>(
    '/readiness/deep-scan/eligibility',
    authHeaders(apiToken)
  );
  return response.data;
}

/**
 * Start a new Deep Analysis.
 */
export async function startDeepAnalysis(
  industry: Industry,
  userContext: UserContext,
  apiToken: string,
  storeReport = false
): Promise<StartAnalysisResponse> {
  const response = await api.post<StartAnalysisResponse>(
    '/readiness/deep-scan/analyses',
    {
      industry,
      store_report: storeReport,
      ...userContext,
    },
    authHeaders(apiToken)
  );
  return response.data;
}

/**
 * Poll deep analysis status.
 */
export async function getDeepAnalysisStatus(
  analysisId: string,
  apiToken: string
): Promise<AnalysisStatusResponse> {
  const response = await api.get<AnalysisStatusResponse>(
    `/readiness/deep-scan/analyses/${analysisId}/status`,
    { ...authHeaders(apiToken), timeout: 60000 }
  );
  return response.data;
}

/**
 * Download the deep analysis report as a binary .docx blob.
 */
export async function downloadDeepReport(
  analysisId: string,
  apiToken: string
): Promise<void> {
  const response = await api.get(
    `/readiness/deep-scan/analyses/${analysisId}/report`,
    {
      responseType: 'blob',
      ...authHeaders(apiToken),
    }
  );

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Transformr-Deep-Analysis-Report.docx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Store a deep analysis report to blob storage.
 */
export async function storeDeepReport(
  analysisId: string,
  apiToken: string
): Promise<StoreReportResponse> {
  const response = await api.post<StoreReportResponse>(
    `/readiness/deep-scan/analyses/${analysisId}/store`,
    {},
    authHeaders(apiToken)
  );
  return response.data;
}
